import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@dawn/ui';

import { RoleChips } from '@/components/settings/RoleChips';
import { useAdminsQuery } from '@/hooks/queries/use-admins-query';

export function UsersPage() {
  const { data: admins = [], isPending, isError } = useAdminsQuery();

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAuthMethod = (method: string, provider?: string | null) => {
    if (method === 'sso' && provider) {
      return `SSO (${provider.charAt(0).toUpperCase() + provider.slice(1)})`;
    }
    return method === 'local' ? 'Email/Password' : method.toUpperCase();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Users</h2>
        <p className="text-muted-foreground">Manage admin accounts for your organization</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Admin Accounts</CardTitle>
          <CardDescription>
            Users with administrative access to this dashboard (max 5 per tenant)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPending && (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading admins...</div>
            </div>
          )}

          {isError && (
            <div className="flex items-center justify-center py-8">
              <div className="text-destructive">Failed to load admins</div>
            </div>
          )}

          {!isPending && !isError && admins.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">No admins found</div>
            </div>
          )}

          {!isPending && !isError && admins.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      Auth Method
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      Roles
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      Last Login
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id} className="border-b last:border-0">
                      <td className="px-4 py-3 text-sm">{admin.email}</td>
                      <td className="px-4 py-3 text-sm">
                        {formatAuthMethod(admin.authMethod, admin.ssoProvider)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <RoleChips roles={admin.roles} />
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(admin.lastLogin)}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(admin.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
